import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";

export default function V2Login() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const { completeLoginHandoff, login, pendingHandoff } = useV2Session();
  const copy = getV2Copy(lang);
  const [form, setForm] = useState({ email: "", password: "" });
  const [handoffMode, setHandoffMode] = useState("move");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);
    const result = await login({ email: form.email, password: form.password });
    setLoading(false);

    if (result.ok) {
      navigate("/dashboard");
      return;
    }

    if (result.requiresHandoff) return;
    setError(result.error || "Invalid email or password.");
  };

  const submitHandoff = async () => {
    if (loading) return;
    setError("");
    setLoading(true);
    const result = await completeLoginHandoff(handoffMode);
    setLoading(false);

    if (result.ok) {
      navigate("/dashboard");
      return;
    }

    setError(result.error || "Anonymous reminder handoff failed. Please try again.");
  };

  return (
    <main className="min-h-screen bg-background px-4 py-12 text-foreground">
      <section className="mx-auto max-w-xl rounded-lg border border-border bg-card p-6">
        <h1 className="mt-3 text-3xl font-extrabold">{copy.login}</h1>
        <p className="mt-3 text-muted-foreground">{copy.authNotes}</p>

        {!pendingHandoff ? (
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="v2-login-email">{copy.email}</Label>
              <Input
                id="v2-login-email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="v2-login-password">{copy.password}</Label>
              <Input
                id="v2-login-password"
                type="password"
                autoComplete="current-password"
                value={form.password}
                onChange={(event) => updateField("password", event.target.value)}
                required
              />
              <div className="text-right text-sm">
                <Link to="/forgot-password" className="font-medium text-primary hover:underline">
                  {copy.forgotPassword}
                </Link>
              </div>
            </div>
            {error && <p className="text-sm font-medium text-destructive">{error}</p>}

            <div className="flex flex-wrap gap-3">
              <Button type="submit" disabled={loading}>
                {loading ? "Signing in..." : copy.continueToDashboard}
              </Button>
              <Link to="/guest">
                <Button type="button" variant="outline" disabled={loading}>
                  {copy.guest}
                </Button>
              </Link>
              <Link to="/register">
                <Button type="button" variant="ghost" disabled={loading}>
                  {copy.register}
                </Button>
              </Link>
            </div>
          </form>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg border border-border bg-background p-4">
              <h2 className="text-lg font-bold">Anonymous data handoff choice</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                This device has active anonymous reminders, Push data, or food logs. Choose how to handle them before signing in.
              </p>
              <label className="mt-4 flex gap-3 text-sm">
                <input
                  type="radio"
                  name="handoff"
                  value="move"
                  checked={handoffMode === "move"}
                  onChange={() => setHandoffMode("move")}
                />
                <span>Move this device's reminders and food logs into my account</span>
              </label>
              <label className="mt-3 flex gap-3 text-sm">
                <input
                  type="radio"
                  name="handoff"
                  value="pause"
                  checked={handoffMode === "pause"}
                  onChange={() => setHandoffMode("pause")}
                />
                <span>Sign in without moving them</span>
              </label>
            </div>
            {error && <p className="text-sm font-medium text-destructive">{error}</p>}
            <Button type="button" onClick={submitHandoff} disabled={loading}>
              {loading ? "Signing in..." : "Continue"}
            </Button>
          </div>
        )}
      </section>
    </main>
  );
}
